import { supabase } from "@/integrations/supabase/client";

/**
 * Utilitário para corrigir compras de conteúdos digitais que ficaram pendentes
 * devido a falhas no webhook do Stripe
 */

export interface PendingPurchaseInfo {
  contentId: string;
  buyerId: string;
  contentTitle: string;
  buyerEmail: string;
  priceInCents: number;
}

/**
 * Força a criação de um registro de compra para um conteúdo digital
 * Usado quando o webhook do Stripe falha
 */
export const createFallbackPurchase = async (info: PendingPurchaseInfo) => {
  console.log("🚨 [fixPendingPurchases] Criando registro de fallback para:", info);

  try {
    // Primeiro verificar se já existe
    const { data: existing, error: checkError } = await supabase
      .from("digital_content_purchases")
      .select("*")
      .eq("digital_content_id", info.contentId)
      .eq("buyer_id", info.buyerId)
      .maybeSingle();

    if (checkError) {
      console.error("❌ Erro ao verificar compra existente:", checkError);
      return { success: false, error: checkError.message };
    }

    if (existing) {
      console.log("ℹ️ Compra já existe, atualizando status...");
      
      const { data: updated, error: updateError } = await supabase
        .from("digital_content_purchases")
        .update({
          payment_status: 'completed',
          access_granted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", existing.id)
        .select();

      if (updateError) {
        console.error("❌ Erro ao atualizar compra:", updateError);
        return { success: false, error: updateError.message };
      }

      console.log("✅ Compra atualizada com sucesso:", updated);
      return { success: true, data: updated[0], action: 'updated' };
    }

    // Criar novo registro
    console.log("➕ Criando novo registro de compra...");
    
    const { data: created, error: createError } = await supabase
      .from("digital_content_purchases")
      .insert({
        digital_content_id: info.contentId,
        buyer_id: info.buyerId,
        purchase_price_cents: info.priceInCents,
        payment_status: 'completed',
        payment_method: 'stripe_manual_fix',
        stripe_payment_intent_id: `manual_fix_${Date.now()}`,
        access_granted_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      })
      .select();

    if (createError) {
      console.error("❌ Erro ao criar compra:", createError);
      return { success: false, error: createError.message };
    }

    console.log("✅ Compra criada com sucesso:", created);
    return { success: true, data: created[0], action: 'created' };

  } catch (error) {
    console.error("💥 Erro geral:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Busca todas as compras de conteúdos digitais de um usuário
 */
export const getUserDigitalContentPurchases = async (userId: string) => {
  console.log("🔍 Buscando compras de conteúdos digitais para usuário:", userId);

  try {
    const { data, error } = await supabase
      .from("digital_content_purchases")
      .select(`
        *,
        digital_content:digital_content_id (
          id,
          title,
          description,
          price_cents,
          file_url,
          cover_image_url
        )
      `)
      .eq("buyer_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("❌ Erro ao buscar compras:", error);
      return { success: false, error: error.message };
    }

    console.log("✅ Compras encontradas:", data?.length || 0);
    return { success: true, data };

  } catch (error) {
    console.error("💥 Erro geral:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Diagnóstico completo de uma compra específica
 */
export const diagnosePurchase = async (contentId: string, buyerId: string) => {
  console.log("🔬 Diagnóstico completo para:", { contentId, buyerId });

  try {
    // 1. Verificar se o conteúdo existe
    const { data: content, error: contentError } = await supabase
      .from("digital_contents")
      .select("*")
      .eq("id", contentId)
      .single();

    // 2. Verificar se o usuário existe
    const { data: user, error: userError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", buyerId)
      .single();

    // 3. Verificar compras existentes
    const { data: purchases, error: purchaseError } = await supabase
      .from("digital_content_purchases")
      .select("*")
      .eq("digital_content_id", contentId)
      .eq("buyer_id", buyerId);

    const diagnosis = {
      contentExists: !contentError && !!content,
      userExists: !userError && !!user,
      purchaseExists: !purchaseError && purchases && purchases.length > 0,
      purchaseCount: purchases?.length || 0,
      contentData: content,
      userData: user,
      purchaseData: purchases,
      errors: {
        content: contentError?.message,
        user: userError?.message,
        purchase: purchaseError?.message
      }
    };

    console.log("📊 Diagnóstico completo:", diagnosis);
    return { success: true, data: diagnosis };

  } catch (error) {
    console.error("💥 Erro no diagnóstico:", error);
    return { success: false, error: error.message };
  }
}; 